/**
 * Blood Pressure Management System (血壓管理系統)
 * Root Application Controller
 * Integrates Password Access Gate (9015), Responsive Viewports,
 * Full CRUD Data Flow, Recharts Trend Visualizations, and CSV Export.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BloodPressureRecord,
  DailyRecordGroup as DailyRecordGroupType,
  RecordFormData,
  DateRangePreset,
} from '@/types/record';
import { getRecords, createRecord, updateRecord, deleteRecord } from '@/services/api';
import { getAuthToken, clearAuthToken, isAuthenticated as checkIsAuthenticated } from '@/services/auth';
import { calculateOverallStatistics } from '@/utils/calculations';
import { groupRecordsByDate, getDateRangeFromPreset, formatDate, getTodayDateString } from '@/utils/dateUtils';
import { downloadCSV } from '@/utils/csvExport';

import { PasswordGate } from '@/components/auth/PasswordGate';
import { RuledHeader } from '@/components/common/RuledHeader';
import { StampBadge } from '@/components/common/StampBadge';
import { Button } from '@/components/common/Button';
import { DesktopLayout } from '@/components/dashboard/DesktopLayout';
import { MobileLayout } from '@/components/mobile/MobileLayout';
import { RecordForm } from '@/components/records/RecordForm';

export const App: React.FC = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return checkIsAuthenticated();
  });

  // Viewport responsive state
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });

  // Records and Data State
  const [allRecords, setAllRecords] = useState<BloodPressureRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Filter State
  const [activePreset, setActivePreset] = useState<DateRangePreset>('30d');
  const [startDate, setStartDate] = useState<string | undefined>(() => {
    return getDateRangeFromPreset('30d').startDate;
  });
  const [endDate, setEndDate] = useState<string | undefined>(() => {
    return getDateRangeFromPreset('30d').endDate;
  });
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modal States
  const [editingRecord, setEditingRecord] = useState<BloodPressureRecord | null>(null);
  const [deletingRecordId, setDeletingRecordId] = useState<number | null>(null);
  const [isNewRecordModalOpen, setIsNewRecordModalOpen] = useState<boolean>(false);
  const [newRecordDefaultDate, setNewRecordDefaultDate] = useState<string | undefined>(undefined);

  // Notification Toast
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3500);
  }, []);

  // Viewport resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobileViewport(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load all records when authenticated
  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getRecords({ order: 'desc', limit: 500 });
      setAllRecords(data);
    } catch (err: any) {
      console.error('Failed to load blood pressure records:', err);
      showToast(err.message || '載入紀錄失敗', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (isAuthenticated) {
      loadRecords();
    }
  }, [isAuthenticated, loadRecords]);

  // Handle Preset Change
  const handlePresetChange = (preset: DateRangePreset) => {
    setActivePreset(preset);
    if (preset === 'all') {
      setStartDate(undefined);
      setEndDate(undefined);
    } else if (preset === 'custom') {
      // Keep existing custom dates or defaults
    } else {
      const range = getDateRangeFromPreset(preset);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
    }
  };

  // Handle Custom Date Range Change
  const handleDateRangeChange = (start: string, end: string) => {
    setActivePreset('custom');
    setStartDate(start);
    setEndDate(end);
  };

  // Handle Filter Reset
  const handleResetFilter = () => {
    setActivePreset('all');
    setStartDate(undefined);
    setEndDate(undefined);
    setSearchQuery('');
  };

  // Filter records based on Date Range and Search Query
  const filteredRecords = useMemo(() => {
    return allRecords.filter((record) => {
      // Date range filtering
      if (startDate && record.measured_date < startDate) {
        return false;
      }
      if (endDate && record.measured_date > endDate) {
        return false;
      }

      // Notes and tags search query filtering
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        const inNotes = (record.notes || '').toLowerCase().includes(q);
        const inDate = record.measured_date.includes(q);
        const inTime = record.measured_time.includes(q);
        if (!inNotes && !inDate && !inTime) {
          return false;
        }
      }

      return true;
    });
  }, [allRecords, startDate, endDate, searchQuery]);

  // Group filtered records by date
  const dailyGroups = useMemo<DailyRecordGroupType[]>(() => {
    return groupRecordsByDate(filteredRecords, 'asc');
  }, [filteredRecords]);

  // Calculate high-level statistics across filtered dataset
  const overallStats = useMemo(() => {
    return calculateOverallStatistics(filteredRecords);
  }, [filteredRecords]);

  // Create Record Handler
  const handleCreateRecord = async (formData: RecordFormData) => {
    setIsSaving(true);
    try {
      await createRecord(formData);
      showToast('血壓紀錄已成功存入檔案！', 'success');
      setIsNewRecordModalOpen(false);
      await loadRecords();
    } catch (err: any) {
      console.error('Failed to create record:', err);
      showToast(err.message || '儲存紀錄失敗', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Update Record Handler
  const handleUpdateRecord = async (formData: RecordFormData) => {
    if (!editingRecord) return;
    setIsSaving(true);
    try {
      await updateRecord(editingRecord.id, formData);
      showToast('血壓紀錄已完成更新！', 'success');
      setEditingRecord(null);
      await loadRecords();
    } catch (err: any) {
      console.error('Failed to update record:', err);
      showToast(err.message || '更新紀錄失敗', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete Record Handler
  const handleConfirmDelete = async () => {
    if (!deletingRecordId) return;
    setIsSaving(true);
    try {
      await deleteRecord(deletingRecordId);
      showToast('紀錄已從檔案中移除', 'info');
      setDeletingRecordId(null);
      await loadRecords();
    } catch (err: any) {
      console.error('Failed to delete record:', err);
      showToast(err.message || '刪除紀錄失敗', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // CSV Export Action
  const handleExportCSV = () => {
    if (filteredRecords.length === 0) {
      showToast('目前篩選條件下無任何紀錄可匯出', 'info');
      return;
    }
    const todayStr = getTodayDateString().replace(/-/g, '');
    downloadCSV(filteredRecords, `blood_pressure_records_${todayStr}.csv`);
    showToast(`已匯出 ${filteredRecords.length} 筆紀錄之 CSV 試算表`, 'success');
  };

  // Print Consultation Action
  const handlePrint = () => {
    window.print();
  };

  // Logout Handler
  const handleLogout = () => {
    clearAuthToken();
    setIsAuthenticated(false);
    showToast('檔案已安全鎖定', 'info');
  };

  // If not authenticated, display the Password Gate (Passcode 9015)
  if (!isAuthenticated) {
    return (
      <PasswordGate
        onSuccess={() => {
          setIsAuthenticated(true);
          showToast('身份驗證通過，檔案已解鎖', 'success');
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-paper-100 text-ink-900 flex flex-col font-sans selection:bg-paper-300">
      {/* ========================================================================= */}
      {/* Top Clinical Header Masthead (Screen Only)                                */}
      {/* ========================================================================= */}
      <header className="bg-paper-100 border-b border-paper-400 py-3 px-4 sm:px-8 no-print sticky top-0 z-30 shadow-xs backdrop-blur-xs bg-paper-100/95">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif font-black text-lg sm:text-xl tracking-wider text-ink-950 uppercase">
              血壓健康管理日誌
            </span>
            <span className="hidden sm:inline-block">
              <StampBadge variant="navy" label="AHA 臨床標準" size="sm" />
            </span>
          </div>

          {/* Top Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden md:inline font-serif text-xs text-ink-500 font-mono">
              AUTH: #9015 • D1 DB READY
            </span>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="hidden sm:inline-flex items-center gap-1"
            >
              <span>🖨 列印就診單</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-1"
            >
              <span>🔒 鎖定檔案</span>
            </Button>
          </div>
        </div>
      </header>

      {/* ========================================================================= */}
      {/* Main Application Workspace                                                */}
      {/* ========================================================================= */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Newspaper Style Ruled Title Banner */}
        <div className="no-print">
          <RuledHeader
            title="BLOOD PRESSURE CLINICAL ARCHIVE • 臨床健康紀錄"
            subtitle="依據 AHA / ACC 醫學指引 • 支援單日多次量測、動脈壓與波動分析"
            metaRight={
              <div className="flex items-center gap-2">
                <StampBadge category="NORMAL" size="sm" />
                <span className="font-mono text-xs text-ink-600 font-bold">
                  {formatDate(new Date(), 'YYYY-MM-DD')}
                </span>
              </div>
            }
            ruleStyle="double"
            size="lg"
          />
        </div>

        {/* Loading Spinner Indicator */}
        {isLoading && (
          <div className="p-4 bg-paper-200 border border-paper-400 rounded-xs text-center text-xs font-serif text-ink-700 flex items-center justify-center gap-2 animate-pulse">
            <span className="w-3 h-3 border-2 border-ink-700 border-t-transparent rounded-full animate-spin" />
            <span>正在同步臨床資料庫檔案...</span>
          </div>
        )}

        {/* Responsive Layout Switching (Mobile vs Desktop) */}
        {isMobileViewport ? (
          <MobileLayout
            records={filteredRecords}
            allRecords={allRecords}
            dailyGroups={dailyGroups}
            stats={overallStats}
            activePreset={activePreset}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            onPresetChange={handlePresetChange}
            onDateRangeChange={handleDateRangeChange}
            onSearchChange={setSearchQuery}
            onResetFilter={handleResetFilter}
            onExportCSV={handleExportCSV}
            onCreateRecord={handleCreateRecord}
            onEditRecord={(rec) => setEditingRecord(rec)}
            onDeleteRecord={(id) => setDeletingRecordId(id)}
            onLogout={handleLogout}
            isLoading={isSaving}
          />
        ) : (
          <DesktopLayout
            records={filteredRecords}
            allRecords={allRecords}
            dailyGroups={dailyGroups}
            stats={overallStats}
            activePreset={activePreset}
            startDate={startDate}
            endDate={endDate}
            searchQuery={searchQuery}
            onPresetChange={handlePresetChange}
            onDateRangeChange={handleDateRangeChange}
            onSearchChange={setSearchQuery}
            onResetFilter={handleResetFilter}
            onExportCSV={handleExportCSV}
            onPrint={handlePrint}
            onCreateRecord={handleCreateRecord}
            onEditRecord={(rec) => setEditingRecord(rec)}
            onDeleteRecord={(id) => setDeletingRecordId(id)}
            onAddRecordForDate={(date) => {
              setNewRecordDefaultDate(date);
              setIsNewRecordModalOpen(true);
            }}
            isLoading={isSaving}
          />
        )}

        {/* ========================================================================= */}
        {/* Printable Physical Report Table (@media print only)                       */}
        {/* ========================================================================= */}
        <div className="print-only hidden">
          <div className="border-b-2 border-black pb-4 mb-4 text-center">
            <h1 className="text-2xl font-serif font-black tracking-wider uppercase">
              臨床血壓健康日誌 • 醫師諮詢報告
            </h1>
            <p className="text-xs font-serif text-gray-600 mt-1">
              列印時間：{formatDate(new Date(), 'fullZh')} • 總量測筆數：{filteredRecords.length} 筆
            </p>
          </div>

          {/* Print Summary Metrics */}
          <div className="grid grid-cols-4 gap-2 mb-4 p-2 border border-black text-xs font-mono">
            <div>平均收縮壓: <strong>{overallStats.overallAvgSystolic} mmHg</strong></div>
            <div>平均舒張壓: <strong>{overallStats.overallAvgDiastolic} mmHg</strong></div>
            <div>平均心跳: <strong>{overallStats.overallAvgPulse} bpm</strong></div>
            <div>平均動脈壓 (MAP): <strong>{overallStats.overallMAP} mmHg</strong></div>
          </div>

          {/* Full Table */}
          <table className="w-full border-collapse border border-black text-xs font-serif">
            <thead>
              <tr className="bg-gray-100 border-b border-black">
                <th className="border border-black p-1.5 text-left">測量日期</th>
                <th className="border border-black p-1.5 text-left">時間</th>
                <th className="border border-black p-1.5 text-right">收縮壓 (SYS)</th>
                <th className="border border-black p-1.5 text-right">舒張壓 (DIA)</th>
                <th className="border border-black p-1.5 text-right">心跳 (PULSE)</th>
                <th className="border border-black p-1.5 text-right">脈壓 (PP)</th>
                <th className="border border-black p-1.5 text-left">部位</th>
                <th className="border border-black p-1.5 text-left">備註說明</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map((r) => (
                <tr key={r.id} className="border-b border-gray-300">
                  <td className="border border-black p-1.5 font-mono">{r.measured_date}</td>
                  <td className="border border-black p-1.5 font-mono">{r.measured_time}</td>
                  <td className="border border-black p-1.5 text-right font-mono font-bold">{r.systolic}</td>
                  <td className="border border-black p-1.5 text-right font-mono font-bold">{r.diastolic}</td>
                  <td className="border border-black p-1.5 text-right font-mono">{r.pulse}</td>
                  <td className="border border-black p-1.5 text-right font-mono">{r.systolic - r.diastolic}</td>
                  <td className="border border-black p-1.5">{r.arm === 'left' ? '左手' : r.arm === 'right' ? '右手' : '未指定'}</td>
                  <td className="border border-black p-1.5">{r.notes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* ========================================================================= */}
      {/* Toast Notification Alert                                                  */}
      {/* ========================================================================= */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div
            className={`
              px-4 py-2.5 rounded-xs border-2 shadow-paper-lg font-serif text-xs font-bold tracking-wider flex items-center gap-2
              ${
                toast.type === 'error'
                  ? 'bg-red-50 text-red-950 border-red-800'
                  : toast.type === 'info'
                  ? 'bg-paper-200 text-ink-900 border-paper-400'
                  : 'bg-emerald-50 text-emerald-950 border-emerald-800'
              }
            `}
          >
            <span>{toast.type === 'error' ? '✖' : toast.type === 'info' ? 'ℹ' : '✔'}</span>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* Modal: Edit Existing Record                                               */}
      {/* ========================================================================= */}
      {editingRecord && (
        <RecordForm
          initialRecord={editingRecord}
          isModal={true}
          onSubmit={handleUpdateRecord}
          onCancel={() => setEditingRecord(null)}
          isLoading={isSaving}
          title="編輯血壓紀錄"
        />
      )}

      {/* ========================================================================= */}
      {/* Modal: Add Record For Specific Date                                       */}
      {/* ========================================================================= */}
      {isNewRecordModalOpen && (
        <RecordForm
          isModal={true}
          defaultDate={newRecordDefaultDate}
          onSubmit={handleCreateRecord}
          onCancel={() => setIsNewRecordModalOpen(false)}
          isLoading={isSaving}
          title="登記新量測紀錄"
        />
      )}

      {/* ========================================================================= */}
      {/* Modal: Delete Confirmation Dialog                                         */}
      {/* ========================================================================= */}
      {deletingRecordId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-950/60 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="w-full max-w-md bg-white border-2 border-red-900 rounded-xs shadow-paper-lg p-6 space-y-4">
            <div className="flex items-center gap-2 text-red-900 border-b border-paper-300 pb-2">
              <span className="text-xl">⚠️</span>
              <h3 className="font-serif font-bold text-base uppercase tracking-wider">
                確認刪除此筆血壓紀錄？
              </h3>
            </div>

            <p className="font-serif text-xs text-ink-700 leading-relaxed">
              此操作將自檔案庫中永久移除編號 <strong>#{deletingRecordId}</strong> 之量測紀錄，刪除後將無法還原。
            </p>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDeletingRecordId(null)}
                disabled={isSaving}
              >
                取消保留
              </Button>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={handleConfirmDelete}
                isLoading={isSaving}
              >
                確認刪除
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* Footer                                                                    */}
      {/* ========================================================================= */}
      <footer className="bg-paper-200 border-t border-paper-400 py-4 px-4 sm:px-8 text-center text-xs font-serif text-ink-500 no-print mt-auto">
        <p>
          血壓管理系統 • Cloudflare Pages + D1 (SQLite) + React + Vite + Tailwind CSS • 密碼驗證：9015
        </p>
      </footer>
    </div>
  );
};
